// Admin data-table kit shared by every admin screen (/admin, /admin/users*,
// /admin/orders*, /admin/licenses, /admin/pricing, /admin/coupons,
// /admin/commissions, /admin/webhooks, /admin/audit): the @/ui Table kit inside
// a named, keyboard-scrollable TableWrapper that re-flows into label-over-value
// rows whenever its panel is narrower than the table needs (ui/DESIGN.md §5),
// plus the filter bar and the empty-value dash.
// Structure: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/table.tsx — MIT; the
// collapse follows MohammadShehadeh/hirael
// registry/hirael/bases/radix/blocks/comparison-02/comparison-02.tsx — MIT, keyed
// to a container query on the wrapper instead of a viewport breakpoint, so a
// wide table also stacks beside the 240px sidebar and at 200% zoom.
//
// display:block drops table semantics in some browsers, so every part carries
// its explicit ARIA role; the visually hidden thead keeps the column headers for
// assistive tech and the per-cell label is visual only (aria-hidden).

import { createContext, useContext, type ComponentProps, type ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from "@/ui";
import { cn } from "@/lib/cn";

export interface DataColumn {
  label: string;
  /** Right-align the column in table layout (amounts, counts). */
  align?: "end";
}

/**
 * Panel width below which a table stacks into label-over-value rows. Pick the
 * smallest step that is wider than the table's min-content width in table
 * layout (nowrap dates, badges and one-line row actions; ids and emails break at
 * their separators through BreakableText), so a table never scrolls sideways
 * inside its panel. Reference panel widths: 326px at 360, 590px at 640, 702px
 * at 1024 (beside the sidebar), 958px at 1280, 1118px from 1440. Every list
 * table fits 58rem (928px), so all of them are tables on a 1280 laptop.
 */
export type StackBelow = "40rem" | "58rem";

interface LayoutClasses {
  /** <table> and <tbody>: blocks when stacked. */
  block: string;
  /** <thead>: kept for screen readers only when stacked. */
  head: string;
  /** <tr> as a stacked group of label/value lines (its padding lives in DataRow). */
  row: string;
  /** Every <td>: panel-edge inset in table layout; no padding, border or min
   *  width when stacked. */
  cell: string;
  /** A labelled <td> as a label column + value column when stacked. */
  labelled: string;
  /** An unlabelled <td> (row actions, edit form) as a full-width line when stacked. */
  full: string;
  /** The single cell of a "nothing here" row: the row already has the padding. */
  flush: string;
  /** Right-aligned value in table layout only. */
  end: string;
  /** Row actions: one line in table layout (they may wrap when stacked). */
  actions: string;
  /** The per-cell label: visible only when stacked. */
  label: string;
  /** The value next to a label: nowrap ids, emails and badges may break when stacked. */
  value: string;
}

// Tailwind only generates classes it finds as literal strings, so every step is
// spelled out in full: @min-[…] = table layout, @max-[…] = stacked rows, both
// measured on the TableWrapper (@container), never the viewport.
const LAYOUT: Record<StackBelow, LayoutClasses> = {
  "40rem": {
    block: "@max-[40rem]:block",
    head: "@max-[40rem]:sr-only",
    row: "@max-[40rem]:block @max-[40rem]:border-b @max-[40rem]:border-line @max-[40rem]:last:border-b-0",
    cell: "@min-[40rem]:first:pl-6 @min-[40rem]:last:pr-6 @max-[40rem]:min-w-0 @max-[40rem]:border-b-0 @max-[40rem]:px-0",
    labelled:
      "@max-[40rem]:grid @max-[40rem]:grid-cols-[minmax(0,7rem)_minmax(0,1fr)] @max-[40rem]:items-baseline @max-[40rem]:gap-x-3 @max-[40rem]:py-1",
    full: "@max-[40rem]:block @max-[40rem]:pt-3 @max-[40rem]:pb-1",
    flush: "@max-[40rem]:py-0",
    end: "@min-[40rem]:text-right",
    actions: "@min-[40rem]:*:flex-nowrap",
    label: "@min-[40rem]:hidden",
    value:
      "@max-[40rem]:whitespace-normal @max-[40rem]:wrap-anywhere @max-[40rem]:[&_[data-slot=badge]]:whitespace-normal",
  },
  "58rem": {
    block: "@max-[58rem]:block",
    head: "@max-[58rem]:sr-only",
    row: "@max-[58rem]:block @max-[58rem]:border-b @max-[58rem]:border-line @max-[58rem]:last:border-b-0",
    cell: "@min-[58rem]:first:pl-6 @min-[58rem]:last:pr-6 @max-[58rem]:min-w-0 @max-[58rem]:border-b-0 @max-[58rem]:px-0",
    labelled:
      "@max-[58rem]:grid @max-[58rem]:grid-cols-[minmax(0,7rem)_minmax(0,1fr)] @max-[58rem]:items-baseline @max-[58rem]:gap-x-3 @max-[58rem]:py-1",
    full: "@max-[58rem]:block @max-[58rem]:pt-3 @max-[58rem]:pb-1",
    flush: "@max-[58rem]:py-0",
    end: "@min-[58rem]:text-right",
    actions: "@min-[58rem]:*:flex-nowrap",
    label: "@min-[58rem]:hidden",
    value:
      "@max-[58rem]:whitespace-normal @max-[58rem]:wrap-anywhere @max-[58rem]:[&_[data-slot=badge]]:whitespace-normal",
  },
};

const LayoutContext = createContext<LayoutClasses>(LAYOUT["40rem"]);

export function DataTable({
  label,
  columns,
  stackBelow,
  busy = false,
  children,
}: {
  /** Accessible name of the table and its scroll region (visually hidden). */
  label: string;
  columns: readonly DataColumn[];
  /** Panel width below which the rows stack (see StackBelow). */
  stackBelow: StackBelow;
  /** A refetch is running behind the rows on screen (filter or page change). */
  busy?: boolean;
  children: ReactNode;
}) {
  const layout = LAYOUT[stackBelow];
  return (
    <LayoutContext.Provider value={layout}>
      {/* The wrapper still scrolls as a last resort (data longer than the
          step was sized for); the page itself never does. */}
      <TableWrapper label={label} aria-busy={busy || undefined} className="@container">
        <Table role="table" className={layout.block}>
          <TableCaption className="sr-only">{label}</TableCaption>
          <TableHeader role="rowgroup" className={cn("bg-surface-1", layout.head)}>
            <tr role="row">
              {columns.map((column) => (
                <TableHead
                  key={column.label}
                  role="columnheader"
                  className={cn("first:pl-6 last:pr-6", column.align === "end" && "text-right")}
                >
                  {column.label}
                </TableHead>
              ))}
            </tr>
          </TableHeader>
          <TableBody role="rowgroup" className={layout.block}>
            {children}
          </TableBody>
        </Table>
      </TableWrapper>
    </LayoutContext.Provider>
  );
}

export function DataRow({ className, ...rest }: ComponentProps<"tr">) {
  const layout = useContext(LayoutContext);
  // Padding on a table row is ignored in table layout, so the panel inset (the
  // one AdminPanel titles use) only takes effect once the row stacks.
  return <TableRow role="row" className={cn("px-5 py-3 sm:px-6", layout.row, className)} {...rest} />;
}

export interface DataCellProps extends Omit<ComponentProps<"td">, "align"> {
  /** The column header, repeated beside the value when the rows stack (visual
   *  only). Omit it for cells that take the whole row there (row actions). */
  label?: string;
  /** Matches `DataColumn.align` in table layout; stacked values stay left. */
  align?: "end";
  /** Row actions: keep the buttons on one line in table layout. */
  actions?: boolean;
}

export function DataCell({ label, align, actions, className, children, ...rest }: DataCellProps) {
  const layout = useContext(LayoutContext);
  return (
    <TableCell
      role="cell"
      className={cn(
        layout.cell,
        label ? layout.labelled : layout.full,
        align === "end" && layout.end,
        actions && layout.actions,
        className,
      )}
      {...rest}
    >
      {label ? (
        <>
          {/* Reset everything a value cell may set (weight, mono, nowrap), so
              every label in a row reads the same. */}
          <span
            aria-hidden="true"
            className={cn(
              "font-sans text-sm font-normal whitespace-normal text-fg-2 normal-nums",
              layout.label,
            )}
          >
            {label}
          </span>
          <div className={cn("min-w-0", layout.value)}>{children}</div>
        </>
      ) : (
        children
      )}
    </TableCell>
  );
}

/** Single full-width "nothing here" row inside an otherwise normal table. */
export function DataEmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  const layout = useContext(LayoutContext);
  return (
    <DataRow className="hover:bg-transparent">
      <DataCell colSpan={colSpan} className={cn("text-fg-2", layout.flush)}>
        {children}
      </DataCell>
    </DataRow>
  );
}

/** Empty value: a visible dash, with words for screen readers. */
export function NoValue({ label }: { label: string }) {
  return (
    <span className="text-fg-2">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Labelled group of FilterSelects above a list: full width on phones, 15rem columns from 640px. */
export function FilterBar({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-end gap-4 *:w-full sm:*:w-60">
      {children}
    </div>
  );
}
